/**
 * PAEX mega menu blocks — shared behaviors for the "- PE" header mega blocks.
 * Loaded (deferred) by sections/header.liquid only when the header has at
 * least one paex mega block. Defines:
 *  - <paex-seasonal-hub>    desktop tile-tab switching (Seasonal hub - PE)
 *  - <paex-seasonal-drawer> pinned drawer tiles + slide-over category view
 * plus a design-mode helper that opens the mega panel when one of its blocks
 * is selected in the theme editor.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Seasonal hub - PE — desktop tile tabs                                */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('paex-seasonal-hub')) {
    customElements.define(
      'paex-seasonal-hub',
      class PaexSeasonalHub extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[data-paex-ssn-tab]'));
          if (this.tabs.length === 0) return;

          this.onActivate = this.activateFromEvent.bind(this);
          this.onKeydown = this.handleKeydown.bind(this);

          this.tabs.forEach(
            function (tab) {
              tab.addEventListener('mouseenter', this.onActivate);
              tab.addEventListener('click', this.onActivate);
              tab.addEventListener('focus', this.onActivate);
              tab.addEventListener('keydown', this.onKeydown);
            }.bind(this)
          );
        }

        disconnectedCallback() {
          if (this.tabs) {
            this.tabs.forEach(
              function (tab) {
                tab.removeEventListener('mouseenter', this.onActivate);
                tab.removeEventListener('click', this.onActivate);
                tab.removeEventListener('focus', this.onActivate);
                tab.removeEventListener('keydown', this.onKeydown);
              }.bind(this)
            );
          }
        }

        activateFromEvent(event) {
          this.activate(event.currentTarget);
        }

        handleKeydown(event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          var index = this.tabs.indexOf(event.currentTarget);
          var next =
            event.key === 'ArrowRight'
              ? (index + 1) % this.tabs.length
              : (index - 1 + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.activate(this.tabs[next]);
        }

        activate(tab) {
          if (!tab || tab.classList.contains('is-active')) return;
          this.tabs.forEach(function (candidate) {
            var zone = document.getElementById(
              candidate.getAttribute('aria-controls')
            );
            var active = candidate === tab;
            candidate.classList.toggle('is-active', active);
            candidate.setAttribute('aria-selected', active ? 'true' : 'false');
            // Roving tabindex (APG tabs): only the selected tile is tabbable,
            // so Tab moves from it into its own panel; arrows switch tiles.
            candidate.tabIndex = active ? 0 : -1;
            if (zone) {
              if (active) {
                zone.removeAttribute('hidden');
              } else {
                zone.setAttribute('hidden', '');
              }
            }
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Seasonal hub - PE — pinned drawer tiles + slide-over category view   */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('paex-seasonal-drawer')) {
    customElements.define(
      'paex-seasonal-drawer',
      class PaexSeasonalDrawer extends HTMLElement {
        connectedCallback() {
          this.tiles = Array.from(
            this.querySelectorAll('[data-paex-ssn-panel]')
          );

          if (this.tiles.length === 0) return;

          this.openPanel = null;
          this.onTile = this.handleTile.bind(this);
          this.onBack = this.handleBack.bind(this);
          this.onKeyup = this.handleKeyup.bind(this);
          this.onDrawerToggle = this.handleDrawerToggle.bind(this);

          this.tiles.forEach(
            function (tile) {
              tile.addEventListener('click', this.onTile);
            }.bind(this)
          );
          this.querySelectorAll('[data-paex-ssn-back]').forEach(
            function (button) {
              button.addEventListener('click', this.onBack);
            }.bind(this)
          );
          this.addEventListener('keyup', this.onKeyup);

          // Reset the slide-over whenever the drawer itself closes.
          this.drawerDetails = this.closest('.menu-drawer-details');
          if (this.drawerDetails) {
            this.drawerDetails.addEventListener('toggle', this.onDrawerToggle);
          }
        }

        disconnectedCallback() {
          if (this.tiles) {
            this.tiles.forEach(
              function (tile) {
                tile.removeEventListener('click', this.onTile);
              }.bind(this)
            );
          }
          this.querySelectorAll('[data-paex-ssn-back]').forEach(
            function (button) {
              button.removeEventListener('click', this.onBack);
            }.bind(this)
          );
          this.removeEventListener('keyup', this.onKeyup);
          if (this.drawerDetails) {
            this.drawerDetails.removeEventListener(
              'toggle',
              this.onDrawerToggle
            );
          }
        }

        handleTile(event) {
          var panel = document.getElementById(
            event.currentTarget.getAttribute('data-paex-ssn-panel')
          );
          if (!panel) return;
          // The slide-over stops above the bottom bar (search + store + rows) —
          // measure its real height (store chip wraps 1-2 lines, fonts vary)
          // instead of guessing, or a strip of the root list shows through.
          // The bar is no longer a descendant: it moved out to the LAST li of
          // the drawer list so its touch gestures reach the nav's scroller, so
          // look it up from the drawer, not from this element. The var still
          // goes on `this` — the panels it feeds are inside this element.
          var drawerInner = this.closest('.menu-drawer-inner') || document;
          var footer = drawerInner.querySelector('.paex-ssn-pin__footer');
          if (footer) {
            this.style.setProperty(
              '--paex-ssn-footer-h',
              Math.round(footer.getBoundingClientRect().height) + 'px'
            );
          }
          this.close();
          this.openPanel = panel;
          this.openTile = event.currentTarget;
          panel.removeAttribute('hidden');
          panel.classList.add('is-open');
          event.currentTarget.setAttribute('aria-expanded', 'true');
          var back = panel.querySelector('[data-paex-ssn-back]');
          // House modal pattern: contain focus inside the slide-over so Tab
          // can't land on content buried under the opaque overlay. webvista
          // is a top-level const in global.js (a lexical global, NOT a
          // window property) — probe it with typeof, never via window.
          if (
            typeof webvista !== 'undefined' &&
            typeof webvista.trapFocus === 'function'
          ) {
            webvista.trapFocus(panel, back);
          } else if (back) {
            back.focus();
          }
        }

        handleBack() {
          var tile = this.openTile;
          this.close();
          if (tile) tile.focus();
          this.animateReturn();
        }

        animateReturn() {
          if (
            window.matchMedia &&
            !window.matchMedia('(prefers-reduced-motion: no-preference)').matches
          ) {
            return;
          }
          var nav = this.closest('.menu-drawer-nav');
          if (!nav) return;
          nav.classList.remove('paex-ssn-anim-back');
          void nav.offsetWidth;
          nav.classList.add('paex-ssn-anim-back');
          nav.addEventListener(
            'animationend',
            function () {
              nav.classList.remove('paex-ssn-anim-back');
            },
            { once: true }
          );
        }

        handleKeyup(event) {
          if (!this.openPanel) return;
          if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
          // Close only the slide-over; keep the drawer open (the native
          // header-drawer Escape handler must not see this event).
          event.stopPropagation();
          this.handleBack();
        }

        handleDrawerToggle(event) {
          if (!event.currentTarget.hasAttribute('open')) this.close();
        }

        close() {
          if (!this.openPanel) return;
          if (
            typeof webvista !== 'undefined' &&
            typeof webvista.removeTrapFocus === 'function'
          ) {
            webvista.removeTrapFocus(this.openTile);
          }
          this.openPanel.classList.remove('is-open');
          this.openPanel.setAttribute('hidden', '');
          this.openPanel = null;
          if (this.openTile) {
            this.openTile.setAttribute('aria-expanded', 'false');
            this.openTile = null;
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Theme editor: selecting a mega block opens its panel                 */
  /* ------------------------------------------------------------------ */

  if (
    window.Shopify &&
    window.Shopify.designMode &&
    !window.paexMegaMenuEditorBound
  ) {
    window.paexMegaMenuEditorBound = true;

    var findDropMenu = function (event) {
      var panel =
        event.target && event.target.closest
          ? event.target.closest('.header-submenu.list-menu--mega')
          : null;
      return panel ? panel.closest('drop-menu') : null;
    };

    document.addEventListener('shopify:block:select', function (event) {
      var dropMenu = findDropMenu(event);
      if (dropMenu && typeof dropMenu.open === 'function') dropMenu.open();
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      var dropMenu = findDropMenu(event);
      if (dropMenu && typeof dropMenu.close === 'function') dropMenu.close();
    });
  }
})();
